import { getDefinition } from "./catalog";
import { effectiveDimensions } from "./effective";
import { isJunction } from "./node-kind";
import type { Cable, ComponentNode, System, SystemNode } from "./types";

/** Feeds all share the bar's input post `a` — the rod is common; everything else
 *  gets a stud of its own. */
const FEED_CATEGORIES = new Set([
  "battery",
  "source",
  "charger",
  "solar",
  "engine",
]);

/** A charger's input draws rather than feeds; every other feed category feeds on
 *  either polarity — a − bar's feed is the battery's negative return. */
export function isFeedCable(
  category: string | undefined,
  direction: string | undefined,
): boolean {
  if (!category || !FEED_CATEGORIES.has(category)) return false;
  if (category === "charger") return direction !== "input";
  return true;
}

export type BusbarPolarity = "positive" | "negative" | "ground";

export type ConversionPlan = {
  newNode: ComponentNode;
  updatedCables: Cable[];
};

/**
 * The stud-id pool takes the catalog ids first so existing cable references like
 * "b","c" survive, then synthesizes `stud-N`.
 */
export function planJunctionToBusbar(
  nodeId: string,
  polarity: BusbarPolarity,
  system: System,
  options?: { asFusebox?: boolean },
): ConversionPlan | null {
  const junction = system.nodes.find((n) => n.id === nodeId);
  if (!junction || junction.kind !== "component" || !isJunction(junction)) return null;

  const defId =
    polarity === "negative"
      ? "dist-neg-bus"
      : polarity === "ground"
        ? "dist-chassis-ground"
        : "dist-pos-bus";
  const def = getDefinition(defId);
  if (!def) return null;

  const touchingCables = system.cables.filter(
    (c) => c.fromNodeId === nodeId || c.toNodeId === nodeId,
  );
  const catalogStuds = def.terminals.filter((t) => t.side === "right");
  const inputId = def.terminals.find((t) => t.side === "left")?.id ?? "a";

  const feedSourceOf = (c: Cable): ComponentNode | null => {
    const otherEndId = c.fromNodeId === nodeId ? c.toNodeId : c.fromNodeId;
    const other = system.nodes.find((n) => n.id === otherEndId);
    if (!other || other.kind !== "component") return null;
    const otherDef = getDefinition(other.definitionId, other.customDefinition);
    if (!otherDef) return null;
    const otherTermId = c.fromNodeId === nodeId ? c.toTerminalId : c.fromTerminalId;
    const otherTerm = otherDef.terminals.find((t) => t.id === otherTermId);
    if (!isFeedCable(otherDef.category, otherTerm?.direction)) return null;
    return other;
  };
  const feedCableIds = new Set(
    touchingCables.filter((c) => feedSourceOf(c) !== null).map((c) => c.id),
  );
  const firstFeedIdx = touchingCables.findIndex((c) => feedCableIds.has(c.id));

  const studCableCount = touchingCables.length - feedCableIds.size;
  // Floor at 1 so the bar still has a drop target with no loads on its rod yet.
  const studCount = Math.max(1, studCableCount);

  const studAvail = catalogStuds
    .slice()
    .map((t) => t.id)
    .concat(
      Array.from(
        { length: Math.max(0, studCount - catalogStuds.length) },
        (_, i) => `stud-${catalogStuds.length + i + 1}`,
      ),
    );

  const studByCable = new Map<string, string>();
  let nextStud = 0;
  touchingCables.forEach((c) => {
    if (feedCableIds.has(c.id)) {
      studByCable.set(c.id, inputId);
      return;
    }
    studByCable.set(c.id, studAvail[nextStud++] ?? `stud-${nextStud + 1}`);
  });

  // Mirrors `effectiveDimensions`'s scaling, or the new node misses the old centre.
  const ratio = catalogStuds.length > 0 ? studCount / catalogStuds.length : 1;
  const baseHeight = def.height >= def.width ? def.height * ratio : def.height;
  const baseWidth = def.width >= def.height ? def.width * ratio : def.width;
  const oldCx = junction.position.x + 7;
  const oldCy = junction.position.y + 7;

  // Aim the input side at the feed. Rotation maps 0° → left, 90° → top,
  // 180° → right, 270° → bottom.
  let rotation: 0 | 90 | 180 | 270 = 0;
  if (firstFeedIdx >= 0) {
    const sourceNode = feedSourceOf(touchingCables[firstFeedIdx]);
    if (sourceNode) {
      const sDef = getDefinition(sourceNode.definitionId, sourceNode.customDefinition);
      if (sDef) {
        const sDims = effectiveDimensions(sDef, sourceNode);
        const sCx = sourceNode.position.x + sDims.width / 2;
        const sCy = sourceNode.position.y + sDims.height / 2;
        const dx = sCx - oldCx;
        const dy = sCy - oldCy;
        if (Math.abs(dx) >= Math.abs(dy)) {
          rotation = dx <= 0 ? 0 : 180;
        } else {
          rotation = dy <= 0 ? 90 : 270;
        }
      }
    }
  }

  const rotated = rotation === 90 || rotation === 270;
  const newWidth = rotated ? baseHeight : baseWidth;
  const newHeight = rotated ? baseWidth : baseHeight;
  const newPosition = {
    x: oldCx - newWidth / 2,
    y: oldCy - newHeight / 2,
  };

  const newNode: ComponentNode = {
    id: nodeId,
    kind: "component",
    definitionId: defId,
    position: newPosition,
    ...(junction.parentId ? { parentId: junction.parentId } : {}),
    studCount,
    ...(rotation !== 0 ? { rotation } : {}),
    ...(options?.asFusebox ? { isFusebox: true } : {}),
  };

  const updatedCables = system.cables.map((c) => {
    const newTerm = studByCable.get(c.id);
    if (!newTerm) return c;
    if (c.fromNodeId === nodeId) {
      return { ...c, fromTerminalId: newTerm, waypoints: undefined };
    }
    if (c.toNodeId === nodeId) {
      return { ...c, toTerminalId: newTerm, waypoints: undefined };
    }
    return c;
  });

  return { newNode, updatedCables };
}

export function planBusbarToJunction(
  nodeId: string,
  system: System,
): ConversionPlan | null {
  const node = system.nodes.find((n) => n.id === nodeId);
  if (!node || node.kind !== "component") return null;
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def || def.renderVariant !== "busbar") return null;

  const dims = effectiveDimensions(def, node);
  const cx = node.position.x + dims.width / 2;
  const cy = node.position.y + dims.height / 2;
  const junction: ComponentNode = {
    id: nodeId,
    kind: "component",
    definitionId: "junction",
    position: { x: cx - 7, y: cy - 7 },
    ...(node.parentId ? { parentId: node.parentId } : {}),
  };

  const updatedCables = system.cables.map((c) => {
    if (c.fromNodeId === nodeId) {
      return { ...c, fromTerminalId: "j", waypoints: undefined };
    }
    if (c.toNodeId === nodeId) {
      return { ...c, toTerminalId: "j", waypoints: undefined };
    }
    return c;
  });

  return { newNode: junction as SystemNode as ComponentNode, updatedCables };
}
